import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

// Placeholder image - replace with your actual asset
const FOUNDER_IMAGE = "/daniel-bio.jpg";

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
}

const AboutSection: React.FC = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const timelineData = t("landingPage.about.timeline", {
    returnObjects: true,
  }) as TimelineEvent[];

  // Scroll progress for the vertical line animation
  const { scrollYProgress } = useScroll({
    //@ts-expect-error - target can be null initially
    target: containerRef,
    offset: ["start 0.8", "end 0.5"],
  });

  // Map scroll progress (0 to 1) to height percentage (0% to 100%)
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="about"
      className="py-20 px-4 overflow-hidden bg-transparent text-white"
    >
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start max-w-6xl mx-auto">
          {/* Left Column: Bio & Image */}
          <div className="lg:sticky lg:top-32">
            {/* Image Container */}
            <motion.div
              //@ts-expect-error - Tailwind CSS classes
              className="rounded-2xl overflow-hidden border border-gray-800 max-w-[320px]"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src={FOUNDER_IMAGE}
                alt="Daniel Martellato"
                className="w-full h-auto object-contain"
              />
            </motion.div>

            {/* Intro Text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              //@ts-expect-error - Tailwind CSS classes
              className="mt-6"
            >
              <h3 className="text-2xl font-bold text-white mb-4">
                {t("landingPage.about.greeting")}
              </h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                {t("landingPage.about.intro")}
              </p>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              //@ts-expect-error - Tailwind CSS classes
              className="mt-6"
            >
              <a
                href="/book-a-call"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center gap-2 px-6 py-3 bg-black hover:bg-green-400 text-white hover:text-black font-semibold rounded-full transition-all shadow-[0_0_15px_rgba(34,197,94,0.4)] hover:shadow-[0_0_25px_rgba(34,197,94,0.6)]"
              >
                <span>{t("landingPage.about.cta")}</span>
                <div className="relative w-4 h-4 overflow-hidden">
                  {/* Animated Arrows */}
                  <ArrowRight className="w-4 h-4 absolute transition-transform duration-300 group-hover:translate-x-full" />
                  <ArrowRight className="w-4 h-4 absolute -translate-x-full transition-transform duration-300 group-hover:translate-x-0" />
                </div>
              </a>
            </motion.div>
          </div>

          {/* Right Column: Timeline */}
          <div ref={containerRef} className="relative pl-8 lg:pl-0">
            {/* Background Line (Static Gray) */}
            <div className="absolute left-3 lg:left-3 top-0 bottom-0 w-px bg-gray-800" />

            {/* Animated Progress Line (Gradient Fill) */}
            <motion.div
              style={{ height: lineHeight }}
              //@ts-expect-error - Tailwind CSS classes
              className="absolute left-3 lg:left-3 top-0 w-px bg-primary"
            />

            <div className="space-y-12">
              {timelineData?.map((item: TimelineEvent, index: number) => (
                <TimelineItem key={index} item={item} index={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sub-component for individual timeline entries
const TimelineItem: React.FC<{ item: TimelineEvent; index: number }> = ({
  item,
  index,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      //@ts-expect-error - Tailwind CSS classes
      className="relative pl-8"
    >
      {/* Timeline Dot */}
      <div className="absolute left-[-5px] top-1.5 w-3 h-3 rounded-full bg-primary ring-4 ring-black" />

      <span className="inline-block px-3 py-1 mb-2 text-xs font-semibold tracking-wider text-primary uppercase bg-green-900/20 rounded-full border border-green-900/50">
        {item.year}
      </span>
      <h4 className="text-xl font-bold text-white mb-2">{item.title}</h4>
      <p className="text-gray-400 leading-relaxed">{item.description}</p>
    </motion.div>
  );
};

export { AboutSection as About };
